/**
 * KsefPermissionsSection — uprawnienia KSeF per NIP (Faza 5).
 *
 * Wybór połączenia KSeF (kontekst firmy) → lista nadanych uprawnień podmiotowych + nadanie
 * uprawnienia podmiotowi (NIP + InvoiceRead/InvoiceWrite) + odebranie. Wywołuje żywe API KSeF,
 * więc działa tylko z aktywnym połączeniem (token/certyfikat) i sesją.
 *
 * Teksty PL inline (konwencja gałęzi roboczej — ekstrakcja i18n przed docelowym mergem).
 */

import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { KeyRound, Trash2, UserPlus } from 'lucide-react';

import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { Dropdown } from '@/components/Dropdown';
import { Input } from '@/components/Input';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';

interface ConnectionItem {
  id: string;
  nip: string;
  companyName: string | null;
  isActive: boolean;
}
interface Grant {
  id: string;
  permissionScope: string;
  description?: string | null;
  startDate?: string | null;
  canDelegate?: boolean;
  contextIdentifier?: { type: string; value: string } | null;
}

const labelCls = 'mb-1 block text-xs font-medium text-text-secondary';

export function KsefPermissionsSection() {
  const toast = useToast();
  const qc = useQueryClient();

  const [connectionId, setConnectionId] = useState('');
  const [subjectNip, setSubjectNip] = useState('');
  const [fullName, setFullName] = useState('');
  const [permRead, setPermRead] = useState(true);
  const [permWrite, setPermWrite] = useState(false);
  const [pendingRevoke, setPendingRevoke] = useState<Grant | null>(null);

  const connectionsQuery = useQuery({
    queryKey: ['ksef-connections'],
    queryFn: async () => {
      const res = await api.get<{ connections: ConnectionItem[] }>('/ksef/connections');
      return res.data.connections ?? [];
    },
  });
  const connections = useMemo(() => connectionsQuery.data ?? [], [connectionsQuery.data]);

  // Domyślnie pierwsze aktywne połączenie.
  useEffect(() => {
    if (!connectionId && connections.length > 0) {
      setConnectionId((connections.find((c) => c.isActive) ?? connections[0]!).id);
    }
  }, [connections, connectionId]);

  const grantsKey = ['ksef-permissions', connectionId] as const;
  const grantsQuery = useQuery({
    queryKey: grantsKey,
    enabled: !!connectionId,
    retry: false,
    queryFn: async () => {
      const res = await api.get<{ grants: Grant[] }>(
        `/ksef/permissions?connectionId=${connectionId}`,
      );
      return res.data.grants ?? [];
    },
  });
  const grants = grantsQuery.data ?? [];

  const grantMutation = useMutation({
    mutationFn: async () => {
      const permissions = [
        ...(permRead ? ['InvoiceRead'] : []),
        ...(permWrite ? ['InvoiceWrite'] : []),
      ];
      return api.post('/ksef/permissions', {
        connectionId,
        subjectNip: subjectNip.trim(),
        permissions,
        fullName: fullName.trim() || undefined,
      });
    },
    onSuccess: () => {
      toast.show({
        title: 'Zlecono nadanie uprawnienia (operacja asynchroniczna w KSeF)',
        variant: 'success',
      });
      setSubjectNip('');
      setFullName('');
      qc.invalidateQueries({ queryKey: grantsKey });
    },
    onError: (err) => {
      const code = isAxiosError(err) ? err.response?.data?.error : undefined;
      toast.show({
        title:
          code === 'KSEF_API_ERROR'
            ? 'KSeF odrzucił żądanie — sprawdź połączenie i uprawnienia (CredentialsManage).'
            : 'Nie udało się nadać uprawnienia.',
        variant: 'error',
      });
    },
  });

  const revokeMutation = useMutation({
    mutationFn: async (id: string) =>
      api.delete(`/ksef/permissions/${id}?connectionId=${connectionId}`),
    onSuccess: () => {
      toast.show({ title: 'Zlecono odebranie uprawnienia', variant: 'success' });
      setPendingRevoke(null);
      qc.invalidateQueries({ queryKey: grantsKey });
    },
    onError: () => toast.show({ title: 'Nie udało się odebrać uprawnienia.', variant: 'error' }),
  });

  const canGrant = connectionId && /^\d{10}$/.test(subjectNip.trim()) && (permRead || permWrite);

  return (
    <div className="space-y-3 border-t border-border-primary pt-5">
      <div>
        <h3 className="flex items-center gap-2 text-base font-semibold text-text-primary">
          <KeyRound className="h-4 w-4 text-accent-600" />
          Uprawnienia KSeF (per NIP)
        </h3>
        <p className="text-sm text-text-secondary">
          Nadaj innemu podmiotowi (np. biuru rachunkowemu) prawo do wystawiania lub przeglądania
          faktur w KSeF w imieniu Twojej firmy. Wymaga aktywnego połączenia i uprawnienia
          CredentialsManage w KSeF.
        </p>
      </div>

      {connections.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border-primary p-3 text-sm text-text-muted">
          Brak połączeń KSeF — dodaj połączenie wyżej, aby zarządzać uprawnieniami.
        </div>
      ) : (
        <>
          <div>
            <label className={labelCls}>Połączenie (kontekst firmy)</label>
            <Dropdown
              value={connectionId}
              onChange={setConnectionId}
              options={connections.map((c) => ({
                value: c.id,
                label: `${c.companyName ?? '—'} · NIP ${c.nip}`,
              }))}
              aria-label="Połączenie (kontekst firmy)"
            />
          </div>

          {/* Formularz nadania */}
          <div className="grid grid-cols-2 gap-3 rounded-xl border border-border-primary bg-bg-elevated p-4">
            <div>
              <label className={labelCls}>NIP podmiotu</label>
              <Input
                type="text"
                inputMode="numeric"
                maxLength={10}
                placeholder="1234567890"
                value={subjectNip}
                onChange={(e) => setSubjectNip(e.target.value.replace(/\D/g, '').slice(0, 10))}
              />
            </div>
            <div>
              <label className={labelCls}>Nazwa (opcjonalnie)</label>
              <Input
                type="text"
                placeholder="Biuro Rachunkowe sp. z o.o."
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
              />
            </div>
            <div className="col-span-2 flex items-center gap-4">
              <label className="flex items-center gap-2 text-sm text-text-primary">
                <input
                  type="checkbox"
                  checked={permRead}
                  onChange={(e) => setPermRead(e.target.checked)}
                />
                Przeglądanie faktur (InvoiceRead)
              </label>
              <label className="flex items-center gap-2 text-sm text-text-primary">
                <input
                  type="checkbox"
                  checked={permWrite}
                  onChange={(e) => setPermWrite(e.target.checked)}
                />
                Wystawianie faktur (InvoiceWrite)
              </label>
              <Button
                variant="primary"
                size="sm"
                className="ml-auto"
                leftIcon={<UserPlus className="h-4 w-4" />}
                disabled={!canGrant || grantMutation.isPending}
                loading={grantMutation.isPending}
                onClick={() => grantMutation.mutate()}
              >
                Nadaj
              </Button>
            </div>
          </div>

          {/* Lista nadanych */}
          <div className="space-y-2">
            {grantsQuery.isLoading && <p className="text-sm text-text-muted">Ładowanie…</p>}
            {grantsQuery.isError && (
              <p className="rounded-lg border border-dashed border-danger-300 p-3 text-sm text-danger-600">
                Nie udało się pobrać uprawnień z KSeF — wymaga aktywnego połączenia i sesji.
              </p>
            )}
            {!grantsQuery.isLoading && !grantsQuery.isError && grants.length === 0 && (
              <div className="rounded-lg border border-dashed border-border-primary p-3 text-center text-sm text-text-muted">
                Brak nadanych uprawnień w tym kontekście.
              </div>
            )}
            {grants.map((g) => (
              <div
                key={g.id}
                className="flex items-center gap-3 rounded-lg border border-border-primary p-3"
              >
                <KeyRound className="h-4 w-4 shrink-0 text-accent-600" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-text-primary">
                    {g.contextIdentifier?.value ?? '—'} · {g.permissionScope}
                  </div>
                  <div className="flex items-center gap-2 text-xs text-text-muted">
                    {g.description && <span>{g.description}</span>}
                    {g.startDate && (
                      <span>od {new Date(g.startDate).toLocaleDateString('pl-PL')}</span>
                    )}
                    {g.canDelegate && <span>· z prawem przekazania</span>}
                  </div>
                </div>
                <button
                  type="button"
                  title="Odbierz"
                  className="shrink-0 rounded p-1.5 text-danger-600 hover:bg-bg-tertiary"
                  onClick={() => setPendingRevoke(g)}
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      <ConfirmDialog
        open={pendingRevoke !== null}
        title="Odebrać uprawnienie?"
        message={
          pendingRevoke
            ? `Odebrać uprawnienie ${pendingRevoke.permissionScope} podmiotowi ${pendingRevoke.contextIdentifier?.value ?? ''}?`
            : ''
        }
        confirmLabel="Odbierz"
        variant="danger"
        onConfirm={() => {
          if (pendingRevoke) revokeMutation.mutate(pendingRevoke.id);
        }}
        onClose={() => setPendingRevoke(null)}
      />
    </div>
  );
}

export default KsefPermissionsSection;
